// REFRACTORED FILES
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, Title, Error, useRefresh } from "react-admin";
import { useMediaQuery } from "@material-ui/core";
import { MapDisplay } from "../components/MapDisplay";
import { VehicleListPanel } from "../components/VehicleListPanel";
import { useVehicleHistory } from "../hooks/useVehicleHistory";
import { _log, getCoordinates, initialBounds } from "../utils/logUtils";
import { httpClient } from "../api/dataProvider";
import { baseiUrl } from "../utils/constants";
import L from "leaflet";
import "leaflet-fullscreen/dist/Leaflet.fullscreen.js";
import "leaflet-fullscreen/dist/leaflet.fullscreen.css";
import "leaflet/dist/leaflet.css";
import "leaflet-rotatedmarker";

const VehiclesTrack = () => {
    const refresh = useRefresh();
    const [vehicles, setVehicles] = useState([]);
    const [isListVisible, setListVisible] = useState(false);
    const [isAllSelected, setAllSelected] = useState(true);
    const [selectedVehicleIds, setSelectedVehicleIds] = useState(() => JSON.parse(localStorage.getItem("t_vids") || "[]"));
    const [searchTerm, setSearchTerm] = useState("");
    const [map, setMap] = useState(null);
    const [userLocation, setUserLocation] = useState(null);
    const [locationError, setLocationError] = useState(null);
    const isSmall = useMediaQuery("(max-width:600px)");
    const [selectedLayer, setSelectedLayer] = useState(() => localStorage.getItem("selectedLayer") || "Hybrid");
    const [isWSConnecting, setIsWSConnecting] = useState(false);

    // Stage 4 reference: WS counter
    const wsCount = useRef(0);

    const { data, loading, error } = useQuery({
        type: "getList",
        resource: "assets",
        payload: {
            pagination: { page: 1, perPage: 100 },
            sort: { field: "id", order: "ASC" },
            filter: {},
        },
    });

    const { id: userId, token } = JSON.parse(localStorage.getItem("tetherauth") || "{}");

    // Stage 1 & 2: Use the new centralized logic hook
    // const { todayLogs, yesterdayLogs, oldLogs, fetchLogs, hydrateLogs } = useVehicleHistory(selectedVehicleIds.length === 1 ? selectedVehicleIds[0] : null, token);
    const [focusedId, setFocusedId] = useState(null);
    const { todayLogs, yesterdayLogs, oldLogs, fetchLogs, hydrateLogs } = useVehicleHistory(focusedId, token);

    useEffect(() => {
        if (!navigator.geolocation) {
            setLocationError("Geolocation is not supported.");
            return;
        }
        navigator.geolocation.getCurrentPosition(
            position => setUserLocation({ lat: position.coords.latitude, lon: position.coords.longitude }),
            () => setLocationError("Grant location permission"),
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
        );
    }, []);

    useEffect(() => {
        if (data) {
            setVehicles(data.map(v => ({ ...v, log: v.log || [] })));
            if (data.length === 1 && selectedVehicleIds.length !== 1) setSelectedVehicleIds([data[0].id]);
        }
    }, [data]);

    useEffect(() => {
        localStorage.setItem("t_vids", JSON.stringify(selectedVehicleIds));
    }, [selectedVehicleIds]);

    // useEffect(() => {
    //     if (!token) return;
    //     const socket = new WebSocket((process.env.REACT_APP_WEBSOCKET_URL || "wss://tether.qedron.com/p?token=") + token);

    //     socket.onmessage = event => {
    //         const message = JSON.parse(event.data);
    //         if (message?.log?.length === 1) {
    //             // const log = { ...message.log[0] };
    //             const raw = message.log[0];
    //             const log = { id: raw._id, logDate: raw.logDate, coordinate: raw.coordinate };
    //             // const vid = log.vehicleRef;
    //             const vid = raw.vehicleRef;

    //             // Update marker position
    //             setVehicles(prev => prev.map(v => (v.id === vid ? { ...v, lastLocation: log.coordinate } : v)));

    //             // Stage 4: If selected vehicle, check for 3rd message delta sync
    //             if (selectedVehicleIds.length === 1 && selectedVehicleIds[0] === vid) {
    //                 wsCount.current++;
    //                 if (wsCount.current >= 3) {
    //                     _log("3rd packet: calling delta fetchLogs", vid);
    //                     wsCount.current = 0;
    //                     fetchLogs(vid);
    //                 }
    //             }

    //             // Stage 2: Hydrate master map with the new log
    //             hydrateLogs(vid, [log]);
    //         }
    //     };
    //     return () => socket.close();
    // }, [token, selectedVehicleIds, hydrateLogs, fetchLogs]);
    // 1. Create a reference to hold the socket instance at the top of your component
    const socketRef = useRef(null);

    // 2. Update the useEffect block
    useEffect(() => {
        if (!token) return;

        // Prevent creating duplicate connections if one is already opening/open
        if (socketRef.current && (socketRef.current.readyState === WebSocket.CONNECTING || socketRef.current.readyState === WebSocket.OPEN)) {
            return;
        }
        setIsWSConnecting(false);
        const wsUrl = (process.env.REACT_APP_WEBSOCKET_URL || "wss://tether.qedron.com/p?token=") + token;
        const socket = new WebSocket(wsUrl);
        socketRef.current = socket;

        socket.onmessage = event => {
            try {
                const message = JSON.parse(event.data);
                if (message?.log?.length === 1) {
                    const raw = message.log[0];
                    const log = { id: raw._id, logDate: raw.logDate, coordinate: raw.coordinate };
                    const vid = raw.vehicleRef;

                    setVehicles(prev => prev.map(v => (v.id === vid ? { ...v, lastLocation: log.coordinate } : v)));

                    // Read selectedVehicleIds from a stable local reference if needed,
                    // or safely parse directly from state
                    if (selectedVehicleIds.length === 1 && selectedVehicleIds[0] === vid) {
                        wsCount.current++;
                        if (wsCount.current >= 3) {
                            wsCount.current = 0;
                            fetchLogs(vid);
                        }
                    }
                    hydrateLogs(vid, [log]);
                }
            } catch (err) {
                console.error("Error parsing WebSocket message:", err);
            }
        };

        socket.onopen = () => {
            // Handshake completed successfully
            setIsWSConnecting(false);
        };

        socket.onerror = () => {
            // Connection failed to establish
            setIsWSConnecting(false);
        };

        socket.onerror = error => {
            console.error("WebSocket structural error:", error);
            setIsWSConnecting(false);
        };

        return () => {
            // ONLY close if the socket is actively open.
            // This stops React from killing the connection during the CONNECTING phase.
            if (socket && socket.readyState === WebSocket.OPEN) {
                socket.close();
                socketRef.current = null;
            }
        };
        // REMOVED deep UI array dependencies to isolate the network lifecycle
    }, [token, fetchLogs, hydrateLogs]);

    const filteredVehicles = useMemo(() => vehicles.filter(v => v.plate.toLowerCase().includes(searchTerm.toLowerCase())), [vehicles, searchTerm]);
    const visibleVehicles = useMemo(() => (isAllSelected ? vehicles : vehicles.filter(v => selectedVehicleIds.includes(v.id))), [isAllSelected, selectedVehicleIds, vehicles]);

    const visibleVehicleIdsKey = useMemo(
        () =>
            visibleVehicles
                .map(v => v.id)
                .sort()
                .join(","),
        [visibleVehicles],
    );

    useEffect(() => {
        if (!map) return;
        const vehiclesWithCoords = visibleVehicles.filter(v => v.lastLocation && typeof v.lastLocation === "string" && v.lastLocation.includes(","));
        _log("Map bounds effect triggered for key:", visibleVehicleIdsKey);

        if (vehiclesWithCoords.length === 0) {
            map.flyToBounds(initialBounds, { duration: 1 });
            return;
        }
        if (vehiclesWithCoords.length === 1) {
            const singleVehicleCoord = getCoordinates(vehiclesWithCoords[0].lastLocation);
            if (singleVehicleCoord) map.flyTo(singleVehicleCoord, 17, { duration: 1 });
        } else {
            const bounds = L.latLngBounds(vehiclesWithCoords.map(v => getCoordinates(v.lastLocation)));
            map.flyToBounds(bounds, { padding: [50, 50], duration: 1 });
        }
    }, [visibleVehicleIdsKey, map, vehicles]);

    const dynamicTitle = useMemo(() => {
        if (loading) return "loading...";
        if (isWSConnecting) return "እየተገናኘ...";
        if (visibleVehicles.length === 1) return `${visibleVehicles[0].plate}`;
        return `${isSmall ? "" : "Showing"} ${visibleVehicles.length} Vehicles`; //
    }, [visibleVehicles]);

    useEffect(() => {
        if (visibleVehicles.length === 1) setFocusedId(visibleVehicles[0].id);
    }, [visibleVehicles]);

    // Check if the selected vehicle's log is stale (between 3 minutes and 3 hours old) using latest today log
    const isStale = useMemo(() => {
        if (selectedVehicleIds.length !== 1 || !todayLogs || todayLogs.length === 0) return false;

        // Use the latest log date available for today (the last item in the array)
        const latestTodayLogTime = todayLogs[todayLogs.length - 1]?.logDate;
        if (!latestTodayLogTime) return false;

        const logDate = new Date(latestTodayLogTime);
        const now = new Date();
        const diffInMinutes = (now - logDate) / (1000 * 60);

        return diffInMinutes >= 3 && diffInMinutes <= 180;
    }, [selectedVehicleIds, todayLogs]);

    if (error) return <Error />;

    return (
        <div style={{ position: "relative", height: "100%", width: "100%" }}>
            <Title title={dynamicTitle} />

            {/* Floating Top-Centered Stale Status Refresh Button */}
            {isStale && !loading && (
                <button
                    onClick={() => refresh()}
                    style={{
                        position: "absolute",
                        top: "16px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 1100, // Higher than Leaflet elements
                        backgroundColor: "#FFD700",
                        color: "#000000",
                        border: "none",
                        // padding: "10px 20px",
                        // borderRadius: "24px",
                        padding: "0px 16px",
                        height: "40px",
                        borderRadius: "9999px",

                        fontWeight: "bold",
                        boxShadow: "0px 4px 10px rgba(0,0,0,0.3)",
                        cursor: "pointer",
                        fontSize: "14px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "8px",
                    }}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#121212" style={{ display: "block" }}>
                        <path d="M480-160q-134 0-227-93t-93-227q0-134 93-227t227-93q69 0 132 28.5T720-690v-110h80v280H520v-80h168q-32-56-87.5-88T480-720q-100 0-170 70t-70 170q0 100 70 170t170 70q77 0 139-44t87-116h84q-28 106-114 173t-196 67Z" />
                    </svg>
                    <span style={{ padding: "0px" }}>አድስ</span>
                </button>
            )}
            <MapDisplay
                visibleVehicles={visibleVehicles}
                todayLogs={todayLogs}
                yesterdayLogs={yesterdayLogs}
                oldLogs={oldLogs}
                onIsolate={id => {
                    setSelectedVehicleIds([id]);
                    setFocusedId(id);
                    setAllSelected(false);
                }}
                userLocation={userLocation}
                selectedLayer={selectedLayer}
                setSelectedLayer={setSelectedLayer}
                setMap={setMap}
                userId={userId}
            />
            <VehicleListPanel
                isListVisible={isListVisible}
                setListVisible={setListVisible}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                isSmall={isSmall}
                isAllSelected={isAllSelected}
                handleToggleAll={() => setAllSelected(!isAllSelected)}
                filteredVehicles={filteredVehicles}
                userLocation={userLocation}
                handleToggleVehicle={id => {
                    setSelectedVehicleIds(prev => {
                        const isRemoving = prev.includes(id);
                        const next = isRemoving ? prev.filter(x => x !== id) : [...prev, id];
                        setFocusedId(!isRemoving ? id : next.length === 1 ? next[0] : null);
                        return next;
                    });
                    setAllSelected(false);
                }}
                selectedVehicleIds={selectedVehicleIds}
            />
        </div>
    );
};

export default VehiclesTrack;
